import React from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import Tooltip from "@mui/material/Tooltip";
import { zIndexMap } from "../../utils/zIndexMap";
import { PACKAGE_INDICATORS } from "@directv-digital/dds.services.channel-lineup-provider";
import FusionTheme from "@directv-digital/fusion.themes.fusion";
import {
  colorPalette,
  MBL_SIZE,
  TBL_SIZE
} from "@directv-digital/dds.ui.dtv-theme";
import { channelColWidthStyle } from "../PackageCmprGrid.styles";
import { focusVisibleOutline } from "@/ChnlListingPageV2/utils/focusVisibleOutline";
import { channelInfoAriaLabel } from "./channelInfoAriaLabel";
import ChannelInfoIcon from "@/shared/ChannelInfoIcon";

interface SvodChannelRowProps {
  channel: any;
  chnlPackageData: any[];
  packageLength: number;
  svodRowWidth?: { width: string; flexBasis: string };
  packageIndicatorIconMap: any;
  borderColor?: string;
  textColor?: string;
  isEmbeddedSvod?: boolean;
}

const sizing = {
  root: {
    display: "flex",
    width: "100%",
    margin: "0px !important",
    padding: "0px"
  },
  channelColContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "6px 8px 4px !important",
    textAlign: "center"
  },
  channelIcon: {
    display: "inline-flex",
    verticalAlign: "middle",
    height: "100%", // "58px",
    width: "100%",
    marginBottom: "-10px"
  },
  channelIconLocalRsnIndicatorText: {
    display: "block",
    position: "relative",
    maxWidth: "80px",
    maxHeight: "80px",
    borderRadius: "4px",
    m: "-14px auto 7px auto",
    p: "5px"
  },
  channelName: {
    transition: "max-height 300ms ease-in-out, opacity 300ms ease-in-out"
  },
  channelNum: {
    height: "21px"
  },
  indicatorColBorder: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: {
      xs: "2px",
      sm: "8px"
    }
  }
};

const MuiTooltipStyleOverrides = {
  position: "relative",
  display: "inline-block",
  maxWidth: "224px",
  width: "max-content",
  backgroundColor: `${colorPalette.neutral_white.main} !important`,
  color: "#102641 !important",
  fontWeight: "400 !important",
  fontSize: "14px !important",
  boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.15)",
  overflowWrap: "normal"
};

const SvodChannelRow = ({
  channel,
  chnlPackageData,
  packageLength,
  svodRowWidth,
  packageIndicatorIconMap,
  borderColor,
  textColor
}: SvodChannelRowProps) => {
  const { name, icon } = channel;

  const ChannelIcon = React.useMemo(() => (
      <picture
        style={{
 width: "100%",
height: "100%"
}}
      >
        <source media={MBL_SIZE} srcSet={icon?.mobile || icon?.default} />
        <source media={TBL_SIZE} srcSet={icon?.tablet || icon?.default} />
        <img
          src={icon?.default || icon}
          alt={name}
          width={64}
          height={64}
          draggable={false}
          onError={e => {
            console.error(
              "[PackagesGrid][EmbeddedSvodRow] untracked icon error",
              e
            );
          }}
          style={{
            fontSize: "8px",
            fill: colorPalette?.neutral?.[600],
            position: "relative",
            zIndex: zIndexMap.ChannelIconImg,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block"
          }}
        />
      </picture>
  ), [icon, name]);

  return (
    // [WAT-12043 / YFH-AMP109] Row-level ARIA table roles intentionally not
    // set — see ChannelRow.tsx for the full rationale. AMP109 (native
    // table markup for the channel grid) is DEFERRED to a follow-up
    // ticket; per-column context here is carried by the indicator
    // button's aria-label below (AMP123).
    <DtvBox
      id={`EmbeddedSvodRow-${channel?.name}`}
      sx={{
        height: {
 xs: "80px !important",
sm: "100px !important"
},
        ...sizing.root,
        background: "transparent",
        border: "none",
        borderBottom: `1px solid ${borderColor}`
      }}
    >
      <DtvBox
        sx={{
          ...sizing.channelColContainer,
          border: "none",
          color: "#666666",
          ...channelColWidthStyle,
          p: "0px",
          height: "100%",
          width: "100%"
        }}
      >
        <DtvBox
          sx={{
            zIndex: zIndexMap.channelIcon,
            backgroundColor: "transparent",
            padding: "0px",
            margin: "0px",
            width: "64px",
            height: "64px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            // WAT-11381 (+ linked CHLU bug): a native long-press / double-tap on the logo image
            // starts a text selection / iOS touch-callout / image drag whose active state then
            // swallows subsequent taps, leaving the page uninteractive. Suppress those gestures.
            userSelect: "none",
            WebkitUserSelect: "none",
            WebkitTouchCallout: "none",
            touchAction: "manipulation"
          }}
        >
          <Tooltip
            // [WAT-12043] describeChild for consistency with the button
            // Tooltips below; the channel-logo <img> already carries
            // alt={name} as its own accessible name.
            describeChild
            title={<DtvText content={channel?.name} sx={{ fontSize: "14px" }} />}
            placement="right-start"
            enterTouchDelay={0}
            leaveTouchDelay={2000}
            PopperProps={{
              sx: {
                "& .MuiTooltip-tooltip": {
                  ...MuiTooltipStyleOverrides,
                  transform: "translate(-8px, 50%) !important"
                }
              }
            }}
          >
            {ChannelIcon}
          </Tooltip>
        </DtvBox>
        <>
          {(channel?.category === "LOCAL" || channel?.category === "RSN") && (
            <Tooltip
              // [WAT-12043 / YFH-AMP122] describeChild so the tooltip is the
              // trigger's accessible DESCRIPTION (aria-describedby), not a
              // MUI-injected aria-labelledby that would shadow the button's
              // `aria-label`. See ChannelRow.tsx for the full rationale.
              describeChild
              title={
                channel?.category === "LOCAL" ?
                  "Local channels aren't available everywhere and may vary by location. You may need to be in your billing region (ZIP Code) to watch them." :
                  "Regional Sports Networks aren't available everywhere and may vary by package and location. You may need to be in your billing region (ZIP Code) to watch them."
              }
              placement="right-end"
              enterTouchDelay={0}
              leaveTouchDelay={2000}
              PopperProps={{
                sx: {
                  "& .MuiTooltip-tooltip": {
                    ...MuiTooltipStyleOverrides,
                    transform: "translate(-8px, calc(50% + 14px)) !important"
                  }
                }
              }}
            >
              {/* [WAT-12043 / YFH-AMP122] See ChannelRow.tsx — same fix: the
                  Local/RSN pill was a non-focusable <span>, so keyboard users
                  couldn't reach the info tooltip. Native <button> makes it
                  focusable and MUI Tooltip announces the content via
                  aria-describedby on focus. Aria-label starts with the
                  visible "RSN" text to satisfy WCAG 2.5.3 Label in Name. */}
              <DtvBox
                component="button"
                type="button"
                aria-label={channelInfoAriaLabel(channel?.category)}
                sx={{
                  ...sizing.channelIconLocalRsnIndicatorText,
                  // Fusion palette token — same as ChannelRow's Local/RSN pill.
                  backgroundColor: FusionTheme.fusion.palette.neutral700,
                  display: "flex",
                  alignItems: "center",
                  gap: "2px",
                  zIndex: zIndexMap.channelIconLocalRsnIndicator,
                  border: "none",
                  cursor: "pointer",
                  color: "inherit",
                  font: "inherit",
                  ...focusVisibleOutline()
                }}
              >
                <DtvText
                  content={channel?.category === "LOCAL" ? "Local" : "RSN"}
                  variant="smalltext"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "white.main",
                    textAlign: "center"
                  }}
                />
                <ChannelInfoIcon color={colorPalette.white.main} />
              </DtvBox>
            </Tooltip>
          )}
        </>
      </DtvBox>
      <DtvBox
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          ...svodRowWidth
        }}
      >
        <DtvBox
          sx={{
            ...sizing.root,
            height: "100%",
            background: "transparent",
            border: "none"
          }}
        >
          {channel?.indicators?.map((indicator, ix) => {
            const tooltipText =
              packageIndicatorIconMap?.[indicator]?.tooltipText || "";
            const pkgName = chnlPackageData[ix]?.pkgName || "";

            return (
              // [WAT-12043 / YFH-AMP109] role="cell" intentionally not set
              // — see AMP109 rationale on the row root above. The indicator
              // <button aria-label> carries the per-package context.
              <DtvBox
                id={`${channel?.name}-${ix}`}
                key={`${channel?.name}-icon-${ix}`}
                sx={{
                  ...sizing.indicatorColBorder,
                  flexBasis: `calc(100% / ${packageLength})`,
                  borderLeft: `1px solid ${borderColor}`,
                  borderBottom: "none",
                  color: "#666666",
                  textAlign: "center"
                }}
              >
                {indicator !== null && (
                  <Tooltip
                    // [WAT-12043 / YFH-AMP123] describeChild so the button's
                    // per-channel aria-label survives focus. See
                    // ChannelRow.tsx for the full rationale.
                    describeChild
                    placement="top"
                    enterTouchDelay={0}
                    leaveTouchDelay={2000}
                    title={
                      <DtvBox sx={{ textAlign: "center" }}>
                        <DtvText sx={{ fontSize: "16px !important" }} content={tooltipText} />
                        <DtvText
                          content={pkgName}
                          sx={{ fontSize: "16px !important" }}
                        />
                      </DtvBox>
                    }
                    PopperProps={{
                      sx: {
                        width: "auto !important",
                        marginBottom: "-10px !important",
                        "& .MuiTooltip-tooltip": {
                          ...MuiTooltipStyleOverrides,
                          transform: "translate(-10px, 50%)",
                          fontSize: "16px !important"
                        }
                      }
                    }}
                  >
                    {/* [WAT-12043 / YFH-AMP123] See ChannelRow.tsx — same fix:
                        wrap the non-focusable <img> in a native <button> so the
                        "Included in <pkg>" tooltip is reachable + announced via
                        keyboard. Inner <img> becomes decorative (alt="").
                        `alt()` takes a single pkgName arg; the earlier 2-arg
                        call landed tooltipText in the pkgName slot. Aria-label
                        prepends `channel.name` so screen-reader users can tell
                        otherwise-identical buttons apart across the row set. */}
                    <DtvBox
                      component="button"
                      type="button"
                      aria-label={[channel?.name, packageIndicatorIconMap?.[
                        indicator as PACKAGE_INDICATORS
                      ]?.alt?.(pkgName)].filter(Boolean).join(": ")}
                      sx={{
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        display: "inline-flex",
                        ...focusVisibleOutline()
                      }}
                    >
                      <img
                        src={
                          packageIndicatorIconMap?.[
                            indicator as PACKAGE_INDICATORS
                          ]?.src
                        }
                        alt=""
                        style={{
                          width: "30px",
                          verticalAlign: "middle",
                          display: "inline-flex",
                          color:
                            packageIndicatorIconMap?.[
                              indicator as PACKAGE_INDICATORS
                            ]?.color
                        }}
                      />
                    </DtvBox>
                  </Tooltip>
                )}
              </DtvBox>
            );
          })}
        </DtvBox>
        {channel?.svodDeviceIncludedText && (
          <DtvText
            content={channel?.svodDeviceIncludedText}
            color={textColor}
            variant="body3"
            sx={{
              padding: "4px 20px 4px 20px",
              border: "1px solid #D1D1D1",
              backgroundColor: "#F4F4F4",
              color: "#102641",
              textAlign: "center"
            }}
          />
        )}
      </DtvBox>
    </DtvBox>
  );
};

export const MemoizedSvodChannelRow = React.memo(SvodChannelRow);
